/**
 * `formula` — the computed `display_value`, and monday's own error rendering.
 *
 * **Captured**: a formula whose reference does not resolve paints a ⚠ in the cell with the
 * tooltip *A column named "Numbers" doesn't exist in this board.*
 * (`discovery/ui-reference/editors/formula/open-1.png`). That sentence is produced verbatim by
 * `packages/formula`, and this renderer shows it.
 *
 * The **Formula builder** is monday's editor: a 730 px modal (captured,
 * `editors/formula/page-edit-formula-1.png`) with two tabs — `AI assistant` (monday-only) and
 * `Resources` — a syntax-coloured expression editor and a `Set formula` button. It is opened
 * from `Settings ▸ Customize Formula column`, i.e. it is a **settings dialog**, not a cell
 * editor: the cell itself is read-only, exactly as `writable: false` says.
 */
import {
  evaluateFormulaColumn,
  findColumnType,
  type FormulaColumnValue,
  type FormulaSettings,
} from '@elizmonday/shared';
import { FormulaSettingsDialog } from './SettingsDialog';
import { defineColumnUi } from '../types';
import type { BoardColumn, ItemRow } from '../../data/types';
import type { CellRenderContext } from '../types';

/** Evaluates the expression against the row the grid already holds. */
function evaluateRow(
  settings: FormulaSettings,
  item: ItemRow,
  context: CellRenderContext,
): FormulaColumnValue | null {
  const columns: readonly BoardColumn[] = context.columns ?? [];
  const cells: Record<string, { type: string; text: string | null; number?: number | null; date?: string | null; title?: string; fields?: Record<string, string | number | null> }> = {};
  for (const column of columns) {
    const definition = findColumnType(column.type);
    if (definition === undefined) continue;
    const columnSettings = definition.fromMondaySettings(column.settings ?? {});
    const value = item.values[column.id] ?? null;
    const keys = definition.toSortKeys(value, columnSettings, { timezone: context.timezone });
    const entry: (typeof cells)[string] = {
      type: column.type,
      title: column.title,
      text: definition.toText(value, columnSettings, { timezone: context.timezone }),
      number: keys.number,
      date: keys.date === null ? null : keys.date.toISOString().slice(0, 10),
    };
    // `{timeline#End}` and `{creation_log#Date}` are the subfields the account's formulas use.
    const record = (value ?? {}) as Record<string, unknown>;
    if (column.type === 'timeline') {
      entry.fields = { Start: (record.from as string) ?? null, End: (record.to as string) ?? null };
    }
    if (column.type === 'creation_log') {
      entry.fields = { Date: ((record.created_at as string) ?? '').slice(0, 10) || null };
    }
    cells[column.id] = entry;
  }
  return evaluateFormulaColumn(settings, cells, { now: new Date() });
}

export const formulaColumnUi = defineColumnUi<FormulaColumnValue, FormulaSettings>({
  type: 'formula',
  icon: 'columnGeneric',
  gallery: { group: 'advanced', keywords: ['formula', 'calculation', 'expression', 'compute'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'right',
  groupable: false,

  Cell: ({ value, settings, item, context, column }) => {
    // A materialised row wins; without one (the fixture, an unsaved edit) it is evaluated here.
    let computed: FormulaColumnValue | null;
    try {
      computed = value ?? evaluateRow(settings, item, context);
    } catch (error) {
      /* P-300: an evaluation that throws is this cell's ⚠, never the route's error page. */
      computed = { display_value: '', error: error instanceof Error ? error.message : String(error) } as FormulaColumnValue;
    }
    if (computed === null) return <span className="ez-cell__empty" aria-hidden="true" />;
    if (computed.error) {
      return (
        <span className="ez-formula-error" title={computed.error} data-testid={`formula-error-${column.id}`}>
          ⚠
        </span>
      );
    }
    return (
      <span className="ez-cell__text" data-align="right" title={computed.display_value}>
        {computed.display_value}
      </span>
    );
  },

  SettingsDialog: FormulaSettingsDialog,

  fromClipboard() {
    throw new Error('a formula column is computed');
  },
  toClipboard(value) {
    // The clipboard gets what the cell shows; monday's own `text` is `""` (spec §5.20).
    return value?.display_value ?? '';
  },
  bucketsOf() {
    return [];
  },
});
